/**
 * DataTable.tsx — shared data table (guidelines section 3): columns config,
 * sentence-case ink-2 headers with no band, sticky header, `numeric` columns
 * (right-aligned, tabular-nums), optional row link, sr-only caption, range line.
 *
 * Row link: mark ONE column `link`; the anchor its cell renders is stretched
 * over the whole row (pseudo-element), so the row is clickable while the name
 * link stays the single keyboard/screen-reader target.
 */

import type { ReactNode } from 'react';

export interface DataTableColumn<Row> {
  key: string;
  header: ReactNode;
  renderCell: (row: Row) => ReactNode;
  /** Right-aligned, tabular-nums. */
  numeric?: boolean;
  /** This column's cell holds the row's anchor; it is stretched over the row. */
  link?: boolean;
}

export interface DataTableProps<Row> {
  columns: DataTableColumn<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  /** Screen-reader-only caption (already localised). */
  caption: string;
  /** Range line, e.g. "1-50 de 120" (already localised). */
  rangeLabel?: string;
  'data-testid'?: string;
  rowTestId?: string;
  /** Per-row extras: extra classes and `data-*` attributes. */
  rowProps?: (row: Row) => { className?: string; [attr: `data-${string}`]: string };
}

export const DataTable = <Row,>({
  columns,
  rows,
  rowKey,
  caption,
  rangeLabel = undefined,
  'data-testid': testId,
  rowTestId = undefined,
  rowProps = undefined,
}: DataTableProps<Row>) => (
  <div>
    <div className="max-h-[70vh] overflow-auto rounded-xl ring-1 ring-inset ring-ink-3/10">
      <table className="w-full border-collapse text-left text-body" data-testid={testId}>
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                className={`sticky top-0 z-10 bg-surface-1 px-4 py-2 text-meta font-medium text-ink-2 ${
                  c.numeric ? 'text-right' : ''
                }`}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const { className: rowClass = '', ...rowData } = rowProps?.(row) ?? {};
            return (
              <tr
                key={rowKey(row)}
                data-testid={rowTestId}
                // eslint-disable-next-line react/jsx-props-no-spreading -- data-* attributes only
                {...rowData}
                className={`relative border-t border-outline hover:bg-surface-2/50 ${rowClass}`.trim()}
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={`px-4 py-2 ${c.numeric ? 'text-right tabular-nums' : ''} ${
                      c.link
                        ? '[&_a]:after:absolute [&_a]:after:inset-0 [&_a]:after:content-[""]'
                        : ''
                    }`}
                  >
                    {c.renderCell(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    {rangeLabel ? <p className="mt-2 text-meta text-ink-3 tabular-nums">{rangeLabel}</p> : null}
  </div>
);
